import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { api, API_BASE } from "../api";
import { BOOKING_URL } from "../mock";

const SID_KEY = "fr_chat_sid";
const QUICK = ["What does it cost?", "Do you support pharmacies?", "Schedule a call"];
const getSid = () => {
  let s = localStorage.getItem(SID_KEY);
  if (!s) {
    s = `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SID_KEY, s);
  }
  return s;
};

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[14px] leading-relaxed ${m.role === "user" ? "bg-[var(--fr-primary)] text-white rounded-br-md" : "bg-white border border-[var(--fr-border)] rounded-bl-md prose-chat"}`}>
      {m.role === "user" ? m.content : <ReactMarkdown components={{ a: ({ node, ...p }) => <a {...p} className="text-[var(--fr-primary-dark)] underline" target={p.href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" /> }}>{m.content || "…"}</ReactMarkdown>}
    </div>
  </div>
);

export const ChatWidget = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/assistant/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, []);

  useEffect(() => {
    if (!open || msgs.length || busy) return;
    api.get(`/chat/${getSid()}`).then((r) => setMsgs((m) => (m.length ? m : r.data.messages))).catch(() => {});
  }, [open, msgs.length, busy]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: t }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API_BASE}/api/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: getSid(), message: t, page: pathname }) });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const d = JSON.parse(p.slice(6));
          if (d.t) setMsgs((m) => { const c = [...m]; const last = c[c.length - 1]; if (last?.role === "assistant") c[c.length - 1] = { role: "assistant", content: (last.content || "") + d.t }; else c.push({ role: "assistant", content: d.t }); return c; });
        }
      }
    } catch {
      setMsgs((m) => { const c = [...m]; const msg = { role: "assistant", content: "Sorry — I'm having trouble right now. You can reach the team at sales@fleetroot.com or on WhatsApp +971 55 200 5851." }; if (c[c.length - 1]?.role === "assistant") c[c.length - 1] = msg; else c.push(msg); return c; });
    } finally {
      setBusy(false);
    }
  };

  if (pathname.startsWith("/admin") || !cfg?.enabled) return null;

  return (
    <>
      {open && (
        <div className="fixed z-50 bottom-24 right-4 md:right-6 w-[calc(100vw-2rem)] max-w-[380px] h-[min(600px,calc(100vh-8rem))] card !rounded-2xl shadow-2xl flex flex-col overflow-hidden fade-up" data-testid="chat-panel">
          <div className="flex items-center gap-3 px-4 h-14 bg-[var(--fr-ink)] text-white flex-shrink-0">
            <span className="w-8 h-8 rounded-full bg-[var(--fr-primary)] flex items-center justify-center"><Sparkles size={15} /></span>
            <div className="min-w-0">
              <div className="font-display font-bold text-sm leading-tight">Fleetroot Assistant</div>
              <div className="text-[11px] text-white/60">Online · replies in seconds</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="ml-auto w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center" aria-label="Close chat" data-testid="chat-close"><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[var(--fr-bg)]" data-testid="chat-messages">
            <Bubble m={{ role: "assistant", content: cfg.welcome }} />
            {msgs.map((m, i) => <Bubble key={i} m={m} />)}
            {msgs.length === 0 && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-quick">
                {QUICK.map((q) => <button key={q} type="button" onClick={() => send(q)} className="text-xs font-medium px-3 h-8 rounded-full bg-white border border-[var(--fr-border)] hover:border-[var(--fr-primary)] hover:text-[var(--fr-primary-dark)]" data-testid="chat-quick-btn">{q}</button>)}
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-3 border-t border-[var(--fr-border)] bg-white flex-shrink-0">
            <input className="flex-1 h-10 px-3 rounded-xl border border-[var(--fr-border)] text-sm outline-none focus:border-[var(--fr-primary)]" placeholder="Ask about features, pricing, a demo…" value={input} onChange={(e) => setInput(e.target.value)} disabled={busy} data-testid="chat-input" />
            <button type="submit" disabled={busy || !input.trim()} className="w-10 h-10 rounded-xl bg-[var(--fr-primary)] text-white flex items-center justify-center disabled:opacity-50" aria-label="Send" data-testid="chat-send"><Send size={16} /></button>
          </form>
          <div className="text-[10px] text-center text-[var(--fr-muted)] pb-2 bg-white">
            AI assistant · <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="underline hover:text-[var(--fr-primary-dark)]" data-testid="chat-book-call">book a call</a> · WhatsApp +971 55 200 5851
          </div>
        </div>
      )}
      <button type="button" onClick={() => setOpen((v) => !v)} className="fixed z-50 bottom-6 right-[5.25rem] md:right-24 w-14 h-14 rounded-full bg-[var(--fr-primary)] text-white shadow-lg hover:scale-105 transition-transform flex items-center justify-center" aria-label="Chat with Fleetroot assistant" data-testid="chat-toggle">
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>
    </>
  );
};

export default ChatWidget;
